import React from "react";
import Column from "@amzn/meridian/column";
import Text from "@amzn/meridian/text";
import Box from "@amzn/meridian/box";
import Table, { TableCell, TableRow } from "@amzn/meridian/table";
import Link from "@amzn/meridian/link";
import styles from "./style.module.scss";
import Button from "@amzn/meridian/button";
import Icon from "@amzn/meridian/icon";
import closeSmallTokens from "@amzn/meridian-tokens/base/icon/close-small";
import Row from "@amzn/meridian/row";
import { useSheets } from "../../../features/sheets/hooks";

interface PropertyDetailsSheetProps {
    property: {
        [key: string]: any;
    };
    handleImageDisplay: (status: boolean) => void;
}

const headerColumnWidth = 250;

export const PropertyDetailsSheet: React.FC<PropertyDetailsSheetProps> = ({
    property,
    handleImageDisplay,
}) => {
    const { closeSheetFromStack } = useSheets();

    const getImageLinkText = (property: any) => {
        return `${property.address || ""} - ${property.city || ""}, ${property.state || ""}`;
    };

    const tableRows = Object.entries(property)
        .filter(([key, value]) => {
            return value !== undefined && !["recordId", "enable", "uiEnabled"].includes(key);
        })
        .map(([key, value]) => {
            const displayKey =
                key === "pdfPageS3Keys"
                    ? "Property Image"
                    : key
                          .split(/(?=[A-Z])/)
                          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                          .join(" ");

            const formatValue = (key, value) => {
                if (value === null || value === undefined || value === "None") return "-";

                if (Array.isArray(value)) {
                    return value.join(", ");
                } else if (typeof value === "number") {
                    if (key === "distanceFromSource") {
                        return value === 0 ? "-" : `${value.toFixed(2)} miles`;
                    } else if (key === "pricePerSq") {
                        return `$${value.toFixed(2)}`;
                    } else if (key === "salePrice") {
                        return `$${value.toLocaleString()}`;
                    } else if (key === "landSize") {
                        return `${value.toFixed(2)} acres`;
                    }

                    return value.toString();
                } else if (typeof value === "boolean") {
                    return value ? "Yes" : "No";
                }

                return value.toString();
            };

            if (key === "pdfPageS3Keys") {
                return {
                    key: displayKey,
                    value: value,
                    isImage: true,
                    hasImages: Array.isArray(value) && value.length > 0,
                };
            }

            return {
                key: displayKey,
                value: formatValue(key, value),
                isImage: false,
                hasImages: false,
            };
        });

    return (
        <Column className={styles.sheetContainer}>
            <Box width="50vw" className={styles.contentArea}>
                <Column spacing="400" width="100%">
                    <Row alignmentHorizontal={"justify"} spacingInset={"0 100 0 500"}>
                        <Text type="h400">Property Details</Text>
                        <Button
                            onClick={() => {
                                closeSheetFromStack("propertyDetails");
                            }}
                            type="icon"
                        >
                            <Icon tokens={closeSmallTokens}>Close sheet</Icon>
                        </Button>
                    </Row>

                    <Box spacingInset="0 500 none">
                        <Table showDividers={true} spacing={"small"}>
                            {tableRows.map(({ key, value, isImage, hasImages }) => (
                                <TableRow key={key} highlightOnHover={true}>
                                    <TableCell
                                        width={headerColumnWidth}
                                        backgroundColor="secondary"
                                    >
                                        {key}
                                    </TableCell>
                                    <TableCell>
                                        {isImage ? (
                                            hasImages ? (
                                                <Text type="b300" color="primary">
                                                    <span style={{ textDecoration: "underline" }}>
                                                        <Link
                                                            onClick={() => handleImageDisplay(true)}
                                                        >
                                                            {getImageLinkText(property)}
                                                        </Link>
                                                    </span>
                                                </Text>
                                            ) : (
                                                <Text type="b300" color="primary">
                                                    Not provided
                                                </Text>
                                            )
                                        ) : (
                                            <Text type="b300" color="primary">
                                                {value}
                                            </Text>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </Table>
                    </Box>
                </Column>
            </Box>
        </Column>
    );
};
